import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { fileUrl } from "@/lib/api";
import { fmtINR, fmtUSD } from "@/lib/money";
import { Button } from "@/components/ui/button";

const FREE_SHIPPING = 2000;
const FLAT_SHIPPING = 99;

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  const shipping = subtotal >= FREE_SHIPPING ? 0 : FLAT_SHIPPING;
  const tax = +(subtotal * 0.05).toFixed(2);
  const total = +(subtotal + shipping + tax).toFixed(2);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <ShoppingBag className="w-16 h-16 text-[#E65C00] mx-auto mb-6" />
        <h1 className="font-display text-4xl text-[#2C1810]">Your cart is empty</h1>
        <p className="font-body text-[#5B3D2A] mt-3">Explore our festive handicrafts to fill it with joy.</p>
        <Button data-testid="cart-shop-btn" onClick={() => navigate("/shop")} className="mt-8 rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white px-8 py-6 btn-lift">
          Start shopping <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <h1 className="font-display text-5xl text-[#2C1810] mb-10">Your Cart</h1>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10">
        <div className="space-y-4">
          {items.map((it) => (
            <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="bg-white border border-[#FFD1A6] rounded-2xl p-5 flex gap-5 items-center shadow-warm">
              <Link to={`/product/${it.product_id}`} className="w-24 h-24 rounded-xl overflow-hidden bg-[#FDEFDD] shrink-0">
                <img src={it.image ? fileUrl(it.image) : "https://images.unsplash.com/photo-1605292356183-a77d0a9c9d1d?w=400&q=80"} alt={it.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1">
                <Link to={`/product/${it.product_id}`} className="font-display text-xl text-[#2C1810] hover:text-[#E65C00] transition-colors">{it.name}</Link>
                <div className="text-sm font-body text-[#5B3D2A] mt-1">₹{fmtINR(it.price)} each</div>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center bg-[#FFF3E0] rounded-full">
                    <button data-testid={`cart-minus-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity - 1)} className="w-8 h-8 flex items-center justify-center">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-8 text-center font-body text-sm">{it.quantity}</span>
                    <button data-testid={`cart-plus-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity + 1)} className="w-8 h-8 flex items-center justify-center">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button data-testid={`cart-remove-${it.product_id}`} onClick={() => removeItem(it.product_id)} className="text-xs font-body text-[#4A148C] hover:text-[#E65C00] flex items-center gap-1">
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                </div>
              </div>
              <div className="font-display text-2xl text-[#E65C00] font-semibold">
                ₹{fmtINR(it.price * it.quantity)}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-[#4A148C] to-[#2C0855] rounded-3xl p-8 text-[#FFF9F2] h-fit shadow-warm">
          <div className="font-display text-2xl mb-6">Order summary</div>
          <div className="space-y-3 font-body text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">₹{fmtINR(subtotal)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span data-testid="cart-shipping">{shipping === 0 ? "Free" : `₹${fmtINR(shipping)}`}</span></div>
            <div className="flex justify-between"><span>GST (5%)</span><span data-testid="cart-tax">₹{fmtINR(tax)}</span></div>
          </div>
          <div className="border-t border-[#FFCF52]/30 my-6" />
          <div className="flex justify-between items-baseline mb-1">
            <span className="font-display text-xl">Total</span>
            <span data-testid="cart-total" className="font-display text-3xl text-[#FFB300] font-semibold">₹{fmtINR(total)}</span>
          </div>
          <div className="text-xs font-body text-[#FFCF52]/70 text-right mb-6">≈ ${fmtUSD(total)} USD</div>
          <Button
            data-testid="cart-checkout-btn"
            onClick={() => navigate("/checkout")}
            className="w-full rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white h-12 btn-lift"
          >
            Proceed to checkout <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          {subtotal < FREE_SHIPPING && (
            <p className="text-xs font-body text-[#FFCF52]/80 mt-4 text-center">
              Add ₹{fmtINR(FREE_SHIPPING - subtotal)} more for free shipping
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
